import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Center,
  Drawer,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import KiloSelect from 'components/KiloSelect/KiloSelect';
import LeaderboardBoard from 'components/Leaderboard/LeaderboardBoard';
import { getMovementLabel, MovementWithSection } from 'components/Leaderboard/movementLabel';
import { EnrichedWorkout, enrichSections } from 'components/Workout/logging';
import { useAuth } from 'context/auth-context';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import useClassWorkoutDetails from 'hooks/workout/useClassWorkoutDetails';
import useWorkoutLeaderboard from 'hooks/workoutLeaderboard/useWorkoutLeaderboard';
import BackHeader from 'layouts/MobileLayout/components/BackHeader';
import { useParams } from 'react-router-dom';
import { WORKOUT_LEVEL_LABEL_MAP, WorkoutLevelEnum } from 'types/enums';
import { getAvatarColor } from 'utils/colors';

import AthleteLeaderboardFilterSheet, {
  AthleteLeaderboardAppliedFilters,
} from './AthleteLeaderboardFilterSheet';
import { computeMyRanking, isAthleteInLeaderboard } from './athleteRanking';

const DEFAULT_FILTERS: AthleteLeaderboardAppliedFilters = {
  session: 'this-session',
  gender: 'all',
  level: 'all',
};

// Format matches spec.md §4.3: "Shows [Section Name - Movement Name]" — this join
// order is specific to this screen; getMovementLabel itself (shared with the staff
// Whiteboard, which joins the same two parts the other way around) only derives
// title/meta, not how a surface renders them.
const formatMovementOption = (movement: MovementWithSection): string => {
  const { title, meta } = getMovementLabel(movement);
  return `${meta} - ${title}`;
};

const AthleteLeaderboardScreen = (): JSX.Element => {
  const { eventId } = useParams<{ eventId: string }>();
  const { session } = useAuth();
  const myAthleteId = session?.athlete?.athleteId ?? null;

  const { data: workoutData, isLoading: isWorkoutLoading } = useClassWorkoutDetails({
    kiloClassId: eventId,
  });
  const workout = workoutData?.data;

  const enrichedSections = useMemo<EnrichedWorkout['sections']>(
    () => (workout ? enrichSections(workout.sections) : []),
    [workout],
  );

  const movements = useMemo<MovementWithSection[]>(
    () =>
      [...enrichedSections]
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .flatMap(section =>
          [...section.movements]
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map(m => ({
              ...m,
              sectionName: section.name,
              workoutSectionId: section.workoutSectionId,
              levelScaled: section.levelScaled,
              levelRx: section.levelRx,
              levelRxPlus: section.levelRxPlus,
            })),
        ),
    [enrichedSections],
  );

  const [selectedMovementId, setSelectedMovementId] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<AthleteLeaderboardAppliedFilters>(DEFAULT_FILTERS);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const {
    trackLeaderboardOpened,
    trackLeaderboardFilterSheetOpened,
    trackLeaderboardYourRankingCardTapped,
  } = useAthleteTracking();
  const hasTrackedOpenRef = useRef(false);

  useEffect(() => {
    if (selectedMovementId || movements.length === 0) return;
    setSelectedMovementId(movements[0].workoutSectionMovementId);
  }, [movements, selectedMovementId]);

  useEffect(() => {
    if (hasTrackedOpenRef.current || !workout) return;
    hasTrackedOpenRef.current = true;
    trackLeaderboardOpened({ workout_id: workout.workoutId, workout_name: workout.name });
  }, [workout, trackLeaderboardOpened]);

  const selectedMovement = movements.find(m => m.workoutSectionMovementId === selectedMovementId) ?? null;

  const { data, isLoading: isLeaderboardLoading } = useWorkoutLeaderboard({
    workout: workout?.workoutId,
    workoutSectionMovement: selectedMovementId ?? undefined,
    sessionId: appliedFilters.session === 'this-session' ? eventId : undefined,
    gender: appliedFilters.gender,
    level: appliedFilters.level,
    enabled: !filterSheetOpen,
  });
  const leaderboard = data?.data;

  // Gender/Category can exclude an athlete who did log a result, which reads
  // differently from "never logged" in the not-ranked card below. Only fetched when
  // a non-default filter is actually narrowing the board — otherwise the query above
  // already is the unfiltered baseline, so a second request would be redundant.
  const hasNonDefaultFilters = appliedFilters.gender !== 'all' || appliedFilters.level !== 'all';
  const { data: baselineData } = useWorkoutLeaderboard({
    workout: workout?.workoutId,
    workoutSectionMovement: selectedMovementId ?? undefined,
    sessionId: appliedFilters.session === 'this-session' ? eventId : undefined,
    gender: 'all',
    level: 'all',
    enabled: !filterSheetOpen && hasNonDefaultFilters,
  });

  const myRanking = useMemo(() => computeMyRanking(leaderboard, myAthleteId), [leaderboard, myAthleteId]);

  const hasLoggedAnyResult = useMemo(() => {
    if (myRanking) return true;
    const baseline = hasNonDefaultFilters ? baselineData?.data : leaderboard;
    return isAthleteInLeaderboard(baseline, myAthleteId);
  }, [myRanking, hasNonDefaultFilters, baselineData, leaderboard, myAthleteId]);

  const handleYourRankingCardClick = (): void => {
    trackLeaderboardYourRankingCardTapped({
      rank: myRanking?.entry.rank ?? null,
      is_ranked: !!myRanking,
    });
    if (!myAthleteId) return;
    document
      .querySelector(`[data-athlete-id="${myAthleteId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (isWorkoutLoading) {
    return (
      <>
        <BackHeader title='Leaderboard' />
        <Center sx={{ flexGrow: 1 }}>
          <Loader />
        </Center>
      </>
    );
  }

  if (!workout || movements.length === 0) {
    return (
      <>
        <BackHeader title='Leaderboard' />
        <Center sx={{ flexGrow: 1 }} p='md'>
          <Text color='dimmed' align='center'>
            No workout to show a leaderboard for.
          </Text>
        </Center>
      </>
    );
  }

  const filterChips: string[] = [appliedFilters.session === 'today' ? 'Today' : 'This Session'];
  if (appliedFilters.gender !== 'all') filterChips.push(appliedFilters.gender === 'men' ? 'Men' : 'Women');
  if (appliedFilters.level !== 'all')
    filterChips.push(WORKOUT_LEVEL_LABEL_MAP[appliedFilters.level as WorkoutLevelEnum]);

  return (
    <>
      <BackHeader title='Leaderboard' />
      <Stack className='content' spacing='md'>
        <Stack spacing={6}>
          <Text size='xs' weight={700} color='dimmed' transform='uppercase'>
            Scored Item
          </Text>
          <KiloSelect
            data={movements.map(m => ({
              value: m.workoutSectionMovementId,
              label: formatMovementOption(m),
            }))}
            value={selectedMovementId}
            onChange={setSelectedMovementId}
            size='sm'
          />
        </Stack>

        <UnstyledButton onClick={handleYourRankingCardClick} sx={{ width: '100%' }}>
          <Box
            sx={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.sm,
              padding: theme.spacing.sm,
              borderRadius: theme.radius.md,
              border: `2px solid ${myRanking ? theme.colors.blue[6] : theme.colors.gray[3]}`,
              backgroundColor: myRanking ? theme.colors.blue[0] : theme.white,
            })}
          >
            {myRanking ? (
              <>
                <Avatar radius='xl' color={getAvatarColor(myRanking.entry.athleteId)}>
                  {myRanking.entry.initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Text weight={700} size='md' truncate>
                    {myRanking.entry.name}
                  </Text>
                  {(myRanking.percentile !== null || myRanking.level) && (
                    <Group spacing={4} noWrap>
                      {myRanking.percentile !== null && (
                        <Text size='sm' color='dimmed'>
                          Top {myRanking.percentile}%
                        </Text>
                      )}
                      {myRanking.level && (
                        <Badge size='sm' color='blue' variant='light'>
                          {WORKOUT_LEVEL_LABEL_MAP[myRanking.level]}
                        </Badge>
                      )}
                    </Group>
                  )}
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Text
                    size={10}
                    weight={700}
                    color='dimmed'
                    transform='uppercase'
                    sx={{ letterSpacing: 0.4 }}
                  >
                    Rank
                  </Text>
                  <Text weight={800} size='lg' color='blue'>
                    #{myRanking.entry.rank}
                  </Text>
                </Box>
              </>
            ) : hasLoggedAnyResult ? (
              <Box sx={{ flex: 1, textAlign: 'center', padding: 4 }}>
                <Text weight={700} size='sm'>
                  No ranking under these filters
                </Text>
                <Text size='xs' color='dimmed'>
                  Try adjusting Gender or Category
                </Text>
              </Box>
            ) : (
              <Box sx={{ flex: 1, textAlign: 'center', padding: 4 }}>
                <Text weight={700} size='sm'>
                  Log your results to see your ranking
                </Text>
                <Text size='xs' color='dimmed'>
                  Go to the Results tab to submit
                </Text>
              </Box>
            )}
          </Box>
        </UnstyledButton>

        <Group position='apart' noWrap>
          <Group spacing={6}>
            <Text size='xs' weight={700} color='dimmed' transform='uppercase'>
              All Athletes
            </Text>
            {filterChips.map(chip => (
              <Badge key={chip} size='xs' color='blue' variant='light'>
                {chip}
              </Badge>
            ))}
          </Group>
          <ActionIcon
            size='md'
            variant='subtle'
            aria-label='Filter leaderboard'
            onClick={() => {
              trackLeaderboardFilterSheetOpened();
              setFilterSheetOpen(true);
            }}
          >
            <IconAdjustmentsHorizontal size={18} />
          </ActionIcon>
        </Group>

        {isLeaderboardLoading ? (
          <Center py='lg'>
            <Loader size='sm' />
          </Center>
        ) : (
          <LeaderboardBoard
            groups={leaderboard?.groups ?? []}
            highlightedAthleteId={myAthleteId}
            enlarged
            spacious
            emptyMessage={
              filterChips.length > 1
                ? 'No results match these filters.'
                : 'The leaderboard starts with the first score.'
            }
          />
        )}

        {!!leaderboard?.total && (
          <Text size='xs' color='dimmed' align='center'>
            Showing {leaderboard.total} of {leaderboard.total} athlete{leaderboard.total === 1 ? '' : 's'}
          </Text>
        )}
      </Stack>

      <Drawer
        opened={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        position='bottom'
        size='auto'
        withCloseButton={false}
        padding='md'
      >
        {selectedMovement && (
          <AthleteLeaderboardFilterSheet
            movement={selectedMovement}
            appliedFilters={appliedFilters}
            onApply={filters => {
              setAppliedFilters(filters);
              setFilterSheetOpen(false);
            }}
            onCancel={() => setFilterSheetOpen(false)}
          />
        )}
      </Drawer>
    </>
  );
};

export default AthleteLeaderboardScreen;
